/**
 * Voice Agent Context
 * Provides global access to the voice agent connection state
 */

import { createContext, useContext, ReactNode } from 'react'
import { useOpenAIRealtime } from '@/hooks/useOpenAIRealtime'
import type { TranscriptItem } from '@/types/realtime'

interface VoiceAgentContextType {
  state: {
    isConnected: boolean
    isListening: boolean
    isSpeaking: boolean
    isMuted: boolean
    transcript: TranscriptItem[]
    currentLine: string
    error: Error | null
    sessionId: string | null
  }
  status: 'disconnected' | 'connecting' | 'connected' | 'listening' | 'speaking' | 'processing'
  connect: () => Promise<void>
  disconnect: () => void
  toggleMute: () => void
  clearTranscript: () => void
  sendMessage: (text: string) => void
  isMinimized: boolean
  toggleMinimize: () => void
  sessionDuration: number
}

const VoiceAgentContext = createContext<VoiceAgentContextType | null>(null)

export function VoiceAgentProvider({ children }: { children: ReactNode }) {
  const voiceAgent = useOpenAIRealtime()

  return (
    <VoiceAgentContext.Provider value={voiceAgent}>
      {children}
    </VoiceAgentContext.Provider>
  )
}

export function useVoiceAgent() {
  const context = useContext(VoiceAgentContext)
  if (!context) {
    throw new Error('useVoiceAgent must be used within VoiceAgentProvider')
  }
  return context
}
